<template>
  <div class="contents">
    <div class="layout">

      <UserListHeader title="아티스트 노출 관리" menu="아티스트 노출 관리"/>

      <div class="white-panel mt20">
        <!-- 요약 (테이블 밖 상단 분리) -->
        <div class="exposure-summary">
          <span class="exposure-summary__item">총 아트워크 <b>{{ formatNumber(totalArtworkSize) }}</b></span>
          <span class="exposure-summary__sep">·</span>
          <span class="exposure-summary__item">공개 <b>{{ formatNumber(visibleArtworkSize) }}</b></span>
        </div>

        <DataTable
          :columns="columns"
          :rows="pageRows"
          row-key="id"
          :row-class="(row) => (row.isHidden ? 'bg-hidden' : '')"
          searchable
          search-placeholder="아티스트명 · ID 검색"
          paged
          :page="page"
          :size="size"
          :total-elements="totalElements"
          :total-pages="totalPages"
          :sort-key="sortKey"
          :sort-direction="sortOrder"
          @search="onSearch"
          @update:page="onPage"
          @update:size="onSize"
          @sort="onSort"
        >
          <template #cell-no="{ index }">{{ index + 1 }}</template>

          <template #cell-imageUrl="{ row }">
            <img v-if="row.imageUrl" :src="row.imageUrl" style="width: 90px;" alt="" />
          </template>

          <template #cell-artworkSize="{ row }">
            <span
              class="dt-link"
              @click="router.push({ name: 'UserArtistArtWorkList', params: { userAccountId: row.userAccountId } })"
            >
              {{ row.artworkSize || '없음' }}
            </span>
          </template>

          <template #cell-userAccountId="{ row }">
            {{ row.userAccountId }}
            <a v-show="row.userAccountId" :href="`${platformUrl}/artist/${row.userAccountId}`" target="_blank">🔗</a>
          </template>

          <template #cell-nameKo="{ row }">
            <span
              class="dt-link"
              @click="router.push({ name: 'UserArtistProfileDetail', params: { userAccountId: row.userAccountId } })"
            >
              {{ row.nameKo }}
            </span>
          </template>

          <template #cell-isHidden="{ row }">
            <select v-model="row.isHidden" @change="updateVisibility(row)" :class="{ 'bg-hidden': row.isHidden }">
              <option :value="false">공개</option>
              <option :value="true">비공개</option>
            </select>
          </template>
        </DataTable>
      </div>
    </div>
  </div>
</template>

<script setup>
import UserListHeader from '../user/UserListHeader.vue';
import DataTable from '@/components/common/DataTable.vue';
import { computed, onMounted, ref } from 'vue';
import { apiClient } from '@/services/auth-header';
import { useSort } from '@/composables/useSort';
import { formatNumber } from '@/utils/formatters';
import router from '@/router';

const { sortKey, sortOrder, sortBy } = useSort('createdAt', 'DESC');

// VUE_APP_REDIRECT_URI(OAuth 리다이렉트=어드민 도메인) 폴백 제거 — 운영에서 프론트 대신 어드민으로 가는 버그.
const platformUrl = process.env.VUE_APP_PLATFORM_URL || 'https://mimio.life';

const columns = [
  { key: 'no', label: 'NO' },
  { key: 'imageUrl', label: '이미지' },
  { key: 'artworkSize', label: '아트워크 [연결]' },
  { key: 'userAccountId', label: 'ID [도메인]', sortable: true },
  { key: 'nameKo', label: '아티스트명 [프로필]', sortable: true },
  { key: 'nameEn', label: '아티스트명(영문)', sortable: true },
  { key: 'createdAt', label: '페이지 생성', sortable: true },
  { key: 'updatedAt', label: '업데이트', sortable: true },
  { key: 'isHidden', label: '공개/비공개', sortable: true },
];

// 전량 조회 후 클라이언트 페이징(총/공개 합계는 전체 기준이라 전량 필요)
const allItems = ref([]);
const keyword = ref(''); // 검색어(아티스트명·ID) — 클라이언트 필터
const page = ref(0); // 0-based
const size = ref(20);

// 검색어로 필터된 집합. 페이징·요약(총/공개) 모두 이 집합 기준.
const filteredItems = computed(() => {
  const kw = keyword.value.trim().toLowerCase();
  if (!kw) return allItems.value;
  return allItems.value.filter((it) =>
    [it.nameKo, it.nameEn, it.userAccountId]
      .some((v) => v != null && String(v).toLowerCase().includes(kw))
  );
});

const totalElements = computed(() => filteredItems.value.length);
const totalPages = computed(() => Math.ceil(filteredItems.value.length / size.value) || 1);
const pageRows = computed(() => {
  const start = page.value * size.value;
  return filteredItems.value.slice(start, start + size.value);
});

// 요약(총/공개)은 검색과 무관하게 항상 전체 기준
const totalArtworkSize = computed(() => allItems.value.reduce((sum, it) => sum + (it.artworkSize || 0), 0));
const visibleArtworkSize = computed(() =>
  allItems.value.reduce((sum, it) => (!it.isHidden ? sum + (it.artworkSize || 0) : sum), 0)
);

const fetchItems = async () => {
  try {
    const response = await apiClient.get('/artists', {
      params: { sort: sortKey.value, direction: sortOrder.value },
    });
    allItems.value = response.data;
    page.value = 0; // 정렬 바뀌면 첫 페이지로
  } catch (error) {
    console.error('Error:', error);
  }
};

const onSearch = (kw) => {
  keyword.value = kw;
  page.value = 0; // 검색 바뀌면 첫 페이지로
};
const onPage = (p) => {
  page.value = p;
};
const onSize = (s) => {
  size.value = Number(s);
  page.value = 0;
};
const onSort = (key) => {
  sortBy(key, fetchItems); // 서버 정렬 + 재조회
};

const updateVisibility = async (item) => {
  try {
    await apiClient.put(`/artists/${item.id}/visibility/${item.isHidden}`);
  } catch (error) {
    console.error('❌ 업데이트 실패', error);
    alert('변경 중 오류가 발생했습니다.');
  }
};

onMounted(async () => {
  await fetchItems();
});
</script>

<style scoped>
.exposure-summary {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 4px 14px;
  font-size: 14px;
  color: #4b5563;
}
.exposure-summary__item b {
  color: #101733;
  font-weight: 700;
}
.exposure-summary__sep {
  color: #c7c7cc;
}
.dt-link {
  color: #2563eb;
  cursor: pointer;
  text-decoration: underline;
}
</style>
